import { useEffect, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChefHat, Plus, Trash2 } from "lucide-react";
import type { KitchenConfigDto, KitchenStationDto } from "@dishfolio/contracts";
import { Button, Card, CardDescription, CardHeader, CardTitle, ConfirmDialog, EmptyState, Field, Input, NativeSelect, toast } from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { useRestaurants } from "../lib/queries.ts";
import { PageContainer, PageHeader } from "../components/Page.tsx";
import { notifyError, UpgradeCard } from "../components/Upgrade.tsx";

export function KitchenSetupPage() {
  const { restaurantId } = useParams({ from: "/app/restaurants/$restaurantId/kitchen" });
  const { org, can } = useOrg();
  const restaurants = useRestaurants();
  const r = restaurants.data?.find((x) => x.id === restaurantId);
  const qc = useQueryClient();
  const key = ["kitchen", restaurantId];
  const config = useQuery({ queryKey: key, queryFn: () => api.get<KitchenConfigDto>(`/restaurants/${restaurantId}/kitchen`), enabled: Boolean(org?.plan.tableOrdering) });
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const [name, setName] = useState("");
  const [del, setDel] = useState<KitchenStationDto | null>(null);
  const [timers, setTimers] = useState({ warnMinutes: 10, lateMinutes: 20 });
  useEffect(() => {
    if (config.data) setTimers({ warnMinutes: config.data.warnMinutes, lateMinutes: config.data.lateMinutes });
  }, [config.data?.warnMinutes, config.data?.lateMinutes]);

  if (org && !org.plan.tableOrdering) {
    return (
      <PageContainer>
        <PageHeader title="Kitchen" />
        <UpgradeCard plan="Business" title="Kitchen display" description="Route table orders to kitchen stations with live tickets, timers and one-tap bump." />
      </PageContainer>
    );
  }
  const manage = can("kitchen:manage");
  const cfg = config.data;
  const assign = async (body: { categories?: Array<{ id: string; stationId: string | null }>; items?: Array<{ id: string; stationId: string | null }> }) => {
    try {
      qc.setQueryData(key, await api.put<KitchenConfigDto>(`/restaurants/${restaurantId}/stations/assignments`, body));
    } catch (err) {
      notifyError(err);
    }
  };
  const stationName = (id: string | null) => cfg?.stations.find((s) => s.id === id)?.name;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={r?.name}
        title="Kitchen"
        description="Tickets from table orders go to a kitchen screen. Route each category to a station, and every station gets its own screen."
        actions={
          <Button asChild>
            <Link to="/kitchen" search={{ restaurantId }}>
              <ChefHat /> Open kitchen display
            </Link>
          </Button>
        }
      />
      <div className="grid gap-6">
        <Card className="gap-4">
          <CardHeader>
            <CardTitle>Stations</CardTitle>
            <CardDescription>For example Grill, Pizza, Bar or Pastry. You can skip stations entirely: the pass (“All”) screen always shows every ticket.</CardDescription>
          </CardHeader>
          {cfg?.stations.length ? (
            <ul className="grid gap-2">
              {cfg.stations.map((s) => (
                <li key={s.id} className="flex items-center gap-2">
                  <Input
                    defaultValue={s.name}
                    disabled={!manage}
                    aria-label="Station name"
                    onBlur={async (e) => {
                      const v = e.target.value.trim();
                      if (!v || v === s.name) return;
                      try {
                        await api.patch(`/restaurants/${restaurantId}/stations/${s.id}`, { name: v });
                        await refresh();
                      } catch (err) {
                        notifyError(err);
                      }
                    }}
                  />
                  {manage ? (
                    <Button variant="ghost" size="icon" aria-label={`Delete ${s.name}`} onClick={() => setDel(s)}>
                      <Trash2 />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState className="p-6" title="No stations yet" description="Every ticket shows on the pass screen. Add stations to split the work." />
          )}
          {manage ? (
            <form
              className="flex items-end gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.post(`/restaurants/${restaurantId}/stations`, { name: name.trim() });
                  setName("");
                  await refresh();
                } catch (err) {
                  notifyError(err);
                }
              }}
            >
              <Field label="New station" className="flex-1">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Grill" maxLength={60} />
              </Field>
              <Button type="submit" variant="outline" disabled={!name.trim()}>
                <Plus /> Add
              </Button>
            </form>
          ) : null}
        </Card>

        <Card className="gap-4">
          <CardHeader>
            <CardTitle>Ticket timers</CardTitle>
            <CardDescription>A ticket turns amber at the warning time and red when it’s late, counted from when the guest ordered.</CardDescription>
          </CardHeader>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api.patch(`/restaurants/${restaurantId}/kitchen`, timers);
                await refresh();
                toast.success("Timers saved");
              } catch (err) {
                notifyError(err);
              }
            }}
          >
            <Field label="Warning after (min)">
              <Input type="number" min={1} max={240} className="w-28" value={timers.warnMinutes} disabled={!manage} onChange={(e) => setTimers({ ...timers, warnMinutes: Number(e.target.value) })} />
            </Field>
            <Field label="Late after (min)">
              <Input type="number" min={2} max={480} className="w-28" value={timers.lateMinutes} disabled={!manage} onChange={(e) => setTimers({ ...timers, lateMinutes: Number(e.target.value) })} />
            </Field>
            {manage ? (
              <Button type="submit" variant="outline">
                Save
              </Button>
            ) : null}
          </form>
        </Card>

        <Card className="gap-4">
          <CardHeader>
            <CardTitle>Routing</CardTitle>
            <CardDescription>
              Pick a station per category; open a category to send single dishes somewhere else. Lines without a station show on the pass only. Changes apply to new orders.
            </CardDescription>
          </CardHeader>
          {!cfg?.stations.length ? (
            <p className="text-sm text-muted-foreground">Add a station first.</p>
          ) : (
            cfg.menus.map((m) => (
              <section key={m.id} className="grid gap-2">
                <h3 className="font-semibold">{m.name}</h3>
                {m.categories.map((c) => (
                  <details key={c.id} className="rounded-lg border">
                    <summary className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2">
                      <span className="font-medium">
                        {c.name} <span className="text-xs text-muted-foreground">· {c.items.length} items{c.items.some((i) => i.stationId) ? " · has overrides" : ""}</span>
                      </span>
                      <NativeSelect
                        className="w-44"
                        aria-label={`Station for ${c.name}`}
                        value={c.stationId ?? ""}
                        disabled={!manage}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => assign({ categories: [{ id: c.id, stationId: e.target.value || null }] })}
                      >
                        <option value="">No station (pass)</option>
                        {cfg.stations.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </NativeSelect>
                    </summary>
                    <ul className="grid gap-1 border-t px-3 py-2">
                      {c.items.map((i) => (
                        <li key={i.id} className="flex items-center justify-between gap-3 text-sm">
                          <span className="truncate">{i.name}</span>
                          <NativeSelect className="w-44" aria-label={`Station for ${i.name}`} value={i.stationId ?? ""} disabled={!manage} onChange={(e) => assign({ items: [{ id: i.id, stationId: e.target.value || null }] })}>
                            <option value="">Same as category{stationName(c.stationId) ? ` (${stationName(c.stationId)})` : ""}</option>
                            {cfg.stations.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </NativeSelect>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}
              </section>
            ))
          )}
        </Card>

        <Card className="gap-2">
          <CardHeader>
            <CardTitle>Running the screens</CardTitle>
          </CardHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>Open the kitchen display on a tablet or a screen with a keyboard, pick the station, and go full screen. Each screen remembers its sound and printing settings.</li>
            <li>Tap a line when it’s done; the bump button finishes the station’s part of the ticket. Keys 1–9 bump tickets in order (USB bump bars work), R recalls the last one.</li>
            <li>Auto-print sends each new ticket to the device’s printer. For printing without a dialog, run Chrome with <code>--kiosk-printing</code>.</li>
            <li>Staff accounts can run the screens; only owners and admins change stations and routing.</li>
          </ul>
        </Card>
      </div>
      <ConfirmDialog
        open={Boolean(del)}
        onOpenChange={(o) => !o && setDel(null)}
        title={`Delete ${del?.name}?`}
        description="Its categories and dishes go back to no station. Tickets already on screen keep their station."
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (!del) return;
          try {
            await api.del(`/restaurants/${restaurantId}/stations/${del.id}`);
            await refresh();
          } catch (err) {
            notifyError(err);
          }
          setDel(null);
        }}
      />
    </PageContainer>
  );
}
